"use client"

import { pickPostImageFormat } from "@/(admin)/modules/ui-builder/config/lib/post-helpers"
import { toImageSrc } from "@/lib/utils"
import { PromotionListContentProps } from "@/types/types"
import Image from "next/image"
import Link from "next/link"
import { useMemo, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import { Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

import PostLink from "@/components/PostLink"
import { getFixedT } from "@/languages/i18n"
import { DefaultImage } from "@/lib/external-components"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import "./promotionList.css"
import { PromotionListSkeleton } from "./PromotionListSkeleton"

export function PromotionListContent({
  id,
  title,
  viewAllLink,
  posts,
  locale,
  loading,
  slidesPerView = 3,
  spaceBetween = 16,
  className = "",
}: PromotionListContentProps) {
  const t = useMemo(() => getFixedT(locale), [locale])
  const swiperRef = useRef<SwiperType | null>(null)
  const [leftButtonPressed, setLeftButtonPressed] = useState(false)
  const [rightButtonPressed, setRightButtonPressed] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const validPosts = useMemo(() => {
    return posts.filter(post => post && post.id)
  }, [posts])

  if (loading) {
    return <PromotionListSkeleton slidesPerView={slidesPerView} spaceBetween={spaceBetween} className={className} />
  }

  if (validPosts.length === 0) {
    return null
  }

  

  return (
    <div id={id} className={`promotion-list-container ${className}`}>
      <div className="container max-w-full sm:max-w-[1304px] mx-auto px-4 sm:px-2 py-8 sm:pt-[1.125rem] sm:pb-0">
        <div className="flex items-start lg:items-center justify-between mb-5 lg:mb-3">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2.5 lg:gap-4">
            <h2 className="text-lg lg:text-2xl leading-[27.9px] lg:leading-[37.2px] font-bold text-[#333]">{title}</h2>
            <button className="px-[18px] py-2 bg-[#f3f3f3] rounded-full leading-3 sm:leading-[1rem] whitespace-nowrap max-h-[32px] text-xs sm:text-[13px] font-semibold text-[#333] hover:text-[#19b04e] hover:bg-[#19b04e]/10 hover:shadow-md hover:shadow-gray-300/50 transition-all duration-300">
              {t("All")}
            </button>
          </div>
          {viewAllLink && (
            <Link
              href={viewAllLink}
              className="text-[#19b04e] text-sm lg:text-base font-normal leading-[21.7px] lg:leading-[24.8px] hover:underline hover:text-[#19b04e]/80"
            >
              {t("View All")}
            </Link>
          )}
        </div>

        <div className="relative pl-0 ">
          <button
            className={`absolute left-3 top-[45%] -translate-y-1/2 z-10 w-[2.375rem] h-[2.375rem] bg-black/30 rounded-full text-white items-center justify-center border-0 cursor-pointer p-0 transition-all duration-300 hidden sm:flex ${
              leftButtonPressed ? "nav-button-pressed" : ""
            }`}
            onClick={() => {
              setLeftButtonPressed(true)
              swiperRef.current?.slidePrev()
              setTimeout(() => setLeftButtonPressed(false), 300)
            }}
            aria-label="Previous slide"
          >
            <svg className="w-6 h-6" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
              />
            </svg>
          </button>
          <button
            className={`absolute right-3 top-[45%] -translate-y-1/2 z-10 w-[2.375rem] h-[2.375rem] bg-black/30 rounded-full text-white items-center justify-center border-0 cursor-pointer p-0 transition-all duration-300 hidden sm:flex ${
              rightButtonPressed ? "nav-button-pressed" : ""
            }`}
            onClick={() => {
              setRightButtonPressed(true)
              swiperRef.current?.slideNext()
              setTimeout(() => setRightButtonPressed(false), 300)
            }}
            aria-label="Next slide"
          >
            <svg className="w-6 h-6" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
              />
            </svg>
          </button>

          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={spaceBetween}
            slidesPerView={1}
            navigation={false}
            onSwiper={swiper => {
              swiperRef.current = swiper
            }}
            pagination={{
              clickable: true,
              dynamicBullets: false,
            }}
            onSlideChange={swiper => {
              setActiveIndex(swiper.realIndex)
            }}
            breakpoints={{
              375: {
                slidesPerView: 1.4,
                spaceBetween: spaceBetween,
              },
              640: {
                slidesPerView: 2,
                spaceBetween: spaceBetween,
              },
              1024: {
                slidesPerView: slidesPerView,
                spaceBetween: spaceBetween,
              },
            }}
            loop={true}
            className="promotion-list-swiper"
          >
            {validPosts.map(post => {
             
              const imageUrl = pickPostImageFormat(post, "large")
              const imageSrc = imageUrl ? toImageSrc(imageUrl) : null

              const postSlug = post.attributes?.slug || post.id.toString()
              const postTitle = post.attributes?.title || post.attributes?.name || ""
              const postId = post.id
              const currentViews = post.attributes?.views
              // Locale của chính bài; PostLink tự prefix as-needed.
              const postLocale = post.attributes?.locale
              return (
                <SwiperSlide key={post.id}>
                  <PostLink
                    href={`/${postSlug}`}
                    locale={postLocale}
                    postId={postId}
                    className="block group relative h-full rounded-lg overflow-hidden"
                  >
                    <div className="relative w-full aspect-[237/160] sm:h-[280px] overflow-hidden rounded-lg">
                      <Image
                        src={imageSrc || DefaultImage}
                        alt={postTitle}
                        fill
                        className="object-cover group-hover:scale-110 transition-transform duration-300"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      />
                    </div>
                  </PostLink>
                </SwiperSlide>
              )
            })}
          </Swiper>

          {/* <div className="promotion-list-swiper swiper-pagination absolute bottom-[55px] z-10 flex justify-center gap-2 md:hidden">
            {Array.from({ length: 5 }).map((_, index) => {
              const totalSlides = validPosts.length
              const slidesPerDot = Math.ceil(totalSlides / 5)
              const startSlide = index * slidesPerDot
              const endSlide = Math.min(startSlide + slidesPerDot - 1, totalSlides - 1)
              const normalizedIndex = activeIndex % totalSlides
              const isActive = normalizedIndex >= startSlide && normalizedIndex <= endSlide

              return (
                <button
                  key={index}
                  className={`swiper-pagination-bullet ${isActive ? "swiper-pagination-bullet-active" : ""}`}
                  onClick={() => {
                    const targetSlide = startSlide
                    swiperRef.current?.slideToLoop(targetSlide)
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              )
            })}
          </div> */}
        </div>
      </div>
    </div>
  )
}
